import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { ChevronRightIcon } from '@radix-ui/react-icons';

import { ErrorDialog } from '../Login/ErrorDialog';
import { useThemeContext } from '../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../utils/fingerprint_utils';
import {
    cpfMask,
    dateMask,
    formatName,
    formatToYYYYMMDD,
    isDate,
    isName,
    isNaturalDocumentNumber,
    nameMask,
} from '../../utils/utils';
import useInput from '../../utils/hooks/useInput';
import { resolveColor } from '../customTheme/theme/utils/resolveColor';
import { PoweredByQITech } from '../customTheme/PrivatePayrollAuction/components/PoweredByQITech';
import { CheckboxField } from '../customTheme/PrivatePayrollAuction/components/CheckboxField';
import { ThemedInput } from '../customTheme/CreditOperationAndSocialSecurityCards/components/ThemedInput';
import { PrivatePayrollAuctionLoading } from '../customTheme/PrivatePayrollAuction/Loading';
import QISignTheme from '../../interfaces/Theme';
import * as S from './styles';

interface EnvelopeFolderLoginProps {
    folderHash: string;
    folderData: any;
    setFolderData: (data: any) => void;
    setFolderStep: (step: string) => void;
    setFolderSessionToken: (token: string) => void;
    folderSessionToken: string | undefined;
    latitude: number | undefined;
    longitude: number | undefined;
    buildAuthHeaders: (token: string) => { headers: { Authorization: string } };
    applyListing: (listing: any) => void;
    onAuthenticated: (nextStep: string, listing: any) => void;
}

const DEFAULT_THEME: QISignTheme = {
    logo_url: 'https://qisign-app-production.s3.amazonaws.com/logos/qi_tech_azul_claro_escuro_principal.png',
    font_color: '#000000',
    button_color: '#4FCCED',
    email_logo_url: 'https://qisign-app-production.s3.amazonaws.com/logos/qi_tech_branco_email.png',
    background_color: '#0E3573',
};

export const EnvelopeFolderLogin = (props: EnvelopeFolderLoginProps) => {
    const {
        folderHash,
        folderData,
        setFolderData,
        setFolderSessionToken,
        latitude,
        longitude,
        applyListing,
        onAuthenticated,
    } = props;

    const { clientThemeSettings } = useThemeContext();
    const resolvedTheme: QISignTheme = clientThemeSettings ?? DEFAULT_THEME;

    const cookieName = `qisign_folder_${folderHash}`;
    const [cookies, setCookie] = useCookies([cookieName]);

    const autofillFiredRef = useRef(false);

    const [loading, setLoading] = useState(false);
    const [lockedFields, setLockedFields] = useState({
        documentNumber: false,
        name: false,
        birthdate: false,
    });
    const [openErrorDialog, setOpenErrorDialog] = useState('');
    const [termsCheckboxChecked, setTermsCheckboxChecked] = useState(false);
    const [inssCheckboxChecked, setInssCheckboxChecked] = useState(false);

    const {
        value: documentNumberValue,
        isValid: documentNumberIsValid,
        hasError: documentNumberHasError,
        valueChangeHandler: documentNumberValueChangeHandler,
        inputBlurHandler: documentNumberInputBlurHandler,
        reset: documentNumberReset,
    } = useInput(isNaturalDocumentNumber);

    const {
        value: birthdateValue,
        isValid: birthdateIsValid,
        hasError: birthdateHasError,
        valueChangeHandler: birthdateValueChangeHandler,
        inputBlurHandler: birthdateInputBlurHandler,
        reset: birthdateReset,
    } = useInput(isDate);

    const {
        value: nameValue,
        isValid: nameIsValid,
        hasError: nameHasError,
        valueChangeHandler: nameValueChangeHandler,
        inputBlurHandler: nameInputBlurHandler,
        reset: nameReset,
    } = useInput(isName);

    const documentNumberRequired = folderData?.document_number_required !== false;
    const birthdateRequired = folderData?.birthdate_required !== false;
    const showBirthdate = documentNumberRequired || birthdateRequired;
    const birthdateOptional = documentNumberRequired && !birthdateRequired;

    const birthdateFieldValid = !showBirthdate
        ? true
        : birthdateOptional
            ? birthdateValue === '' || birthdateIsValid
            : birthdateIsValid;
    const identityValid = documentNumberRequired ? documentNumberIsValid : nameIsValid;

    const bothConsentsChecked = termsCheckboxChecked && inssCheckboxChecked;
    const isSubmitDisabled = !identityValid || !birthdateFieldValid || !bothConsentsChecked || loading;

    const onSubmit = useCallback(
        async (
            documentNumber: string,
            birthdate: string | undefined,
            name: string | undefined,
            alreadyFormatted: boolean,
        ) => {
            let browserKey: string | null = null;
            try {
                browserKey = await getOrCreateBrowserKey();
            } catch (_) {}

            const payload: any = {};

            if (documentNumber) {
                payload.document_number = alreadyFormatted ? documentNumber : cpfMask(documentNumber);
            }

            if (name) {
                payload.name = formatName(name);
            }

            if (birthdate) {
                payload.birthdate = alreadyFormatted ? birthdate : formatToYYYYMMDD(birthdate);
            }

            if (latitude !== undefined && longitude !== undefined) {
                payload.gps_data = { coordinates: [longitude, latitude] };
            }

            if (browserKey) {
                payload.device_data = { device_key: browserKey };
            }

            setLoading(true);

            axios
                .post('/folder_signer/hash/' + folderHash, payload)
                .then((response) => {
                    const token = response.data.folder_session_token;
                    setFolderSessionToken(token);
                    setFolderData({ ...folderData, ...response.data });
                    documentNumberReset();
                    birthdateReset();
                    nameReset();
                    if (response.data.listing) {
                        applyListing(response.data.listing);
                    }
                    setCookie(cookieName, payload, { path: `/ef/${folderHash}`, maxAge: 60 * 60 });
                    const nextStep = response.data.next_step === 'menu'
                        ? 'proposals'
                        : response.data.next_step;
                    onAuthenticated(nextStep, response.data.listing);
                })
                .catch((error) => {
                    const status = error.response?.status;
                    if (status === 403) {
                        setOpenErrorDialog('folder_identity_mismatch');
                    } else if (status === 404) {
                        setOpenErrorDialog('folder_not_found');
                    } else {
                        setOpenErrorDialog('connection_error');
                    }
                    setCookie(cookieName, '', { path: `/ef/${folderHash}`, maxAge: 0 });
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [
            folderHash,
            folderData,
            latitude,
            longitude,
            setFolderData,
            setFolderSessionToken,
            applyListing,
            onAuthenticated,
            documentNumberReset,
            birthdateReset,
            nameReset,
            setCookie,
            cookieName,
        ],
    );

    useEffect(() => {
        if (autofillFiredRef.current) return;
        const params = new URLSearchParams(window.location.search);
        const loginHashParam = params.get('login_hash');

        if (loginHashParam) {
            const autofillData = folderData?.signer_data;
            const autofillIdentity = documentNumberRequired
                ? autofillData?.document_number
                : autofillData?.name;
            if (!autofillIdentity) return;

            autofillFiredRef.current = true;
            const keyToInherit = folderData?.device_data?.device_key ?? null;
            getOrCreateBrowserKey(keyToInherit).catch(() => {});

            const locked = { documentNumber: false, name: false, birthdate: false };
            if (documentNumberRequired && autofillData?.document_number) {
                documentNumberValueChangeHandler({
                    target: { value: cpfMask(autofillData.document_number) },
                } as React.ChangeEvent<HTMLInputElement>);
                locked.documentNumber = true;
            }
            if (!documentNumberRequired && autofillData?.name) {
                nameValueChangeHandler({
                    target: { value: autofillData.name },
                } as React.ChangeEvent<HTMLInputElement>);
                locked.name = true;
            }
            if (showBirthdate && autofillData?.birthdate) {
                const [year, month, day] = String(autofillData.birthdate).split('-');
                if (year && month && day) {
                    birthdateValueChangeHandler({
                        target: { value: `${day}/${month}/${year}` },
                    } as React.ChangeEvent<HTMLInputElement>);
                    locked.birthdate = true;
                }
            }
            setLockedFields(locked);

            const url = new URL(window.location.href);
            url.searchParams.delete('login_hash');
            window.history.replaceState({}, document.title, url.toString());
            return;
        }

        const documentNumberParam = params.get('document_number');
        const birthdateParam = params.get('birthdate');
        const nameParam = params.get('name');

        const identityParam = documentNumberRequired ? documentNumberParam : nameParam;
        if (identityParam) {
            autofillFiredRef.current = true;

            const url = new URL(window.location.href);
            url.searchParams.delete('document_number');
            url.searchParams.delete('birthdate');
            url.searchParams.delete('name');
            window.history.replaceState({}, document.title, url.toString());

            onSubmit(
                documentNumberParam ?? '',
                birthdateParam ?? undefined,
                nameParam ?? undefined,
                false,
            );
            return;
        }

        const cached = cookies[cookieName];
        const cachedIdentity = documentNumberRequired ? cached?.document_number : cached?.name;
        if (cached && cachedIdentity) {
            autofillFiredRef.current = true;
            onSubmit(
                cached.document_number ?? '',
                cached.birthdate ?? undefined,
                cached.name ?? undefined,
                true,
            );
        }
    }, [
        onSubmit,
        documentNumberRequired,
        showBirthdate,
        cookies,
        cookieName,
        folderData,
        documentNumberValueChangeHandler,
        nameValueChangeHandler,
        birthdateValueChangeHandler,
    ]);

    const handleSubmit = () => {
        if (!isSubmitDisabled) {
            onSubmit(
                documentNumberRequired ? documentNumberValue : '',
                showBirthdate ? birthdateValue : undefined,
                documentNumberRequired ? undefined : nameValue,
                false,
            );
        }
    };

    const primary = resolveColor('primary', resolvedTheme);
    const text = resolveColor('text', resolvedTheme);

    if (loading) {
        return <PrivatePayrollAuctionLoading />;
    }

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {resolvedTheme.logo_url && (
                    <S.LogoImage src={resolvedTheme.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.FormContainer>
                <S.IntroSection>
                    <S.Title $color={primary}>
                        {folderData?.login_content?.title ?? 'Bem-vindo'}
                    </S.Title>
                    <S.Subtitle>
                        {folderData?.login_content?.text ?? 'Para acessar os processos de assinatura, preencha os campos abaixo:'}
                    </S.Subtitle>
                </S.IntroSection>

                <S.FormFieldsWrapper
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter') handleSubmit();
                    }}
                >
                    {documentNumberRequired ? (
                        <ThemedInput
                            labelValue="Número do CPF"
                            labelColor="#666"
                            onChange={documentNumberValueChangeHandler}
                            onBlur={documentNumberInputBlurHandler}
                            hasError={documentNumberHasError}
                            fieldValue={cpfMask(documentNumberValue)}
                            errorMessage="Número de CPF inválido."
                            type="numeric"
                            disabled={lockedFields.documentNumber}
                        />
                    ) : (
                        <ThemedInput
                            labelValue="Nome completo"
                            labelColor="#666"
                            onChange={nameValueChangeHandler}
                            onBlur={nameInputBlurHandler}
                            hasError={nameHasError}
                            fieldValue={nameMask(nameValue)}
                            errorMessage="Nome é obrigatório."
                            type="text"
                            disabled={lockedFields.name}
                        />
                    )}
                    {showBirthdate && (
                        <ThemedInput
                            labelValue={birthdateOptional ? 'Data de Nascimento (opcional)' : 'Data de Nascimento (DD/MM/AAAA)'}
                            labelColor="#666"
                            onChange={birthdateValueChangeHandler}
                            onBlur={birthdateInputBlurHandler}
                            hasError={birthdateOptional && birthdateValue === '' ? false : birthdateHasError}
                            fieldValue={dateMask(birthdateValue)}
                            errorMessage="Data de nascimento inválida."
                            type="numeric"
                            disabled={lockedFields.birthdate}
                        />
                    )}
                </S.FormFieldsWrapper>

                <S.CheckboxGroup>
                    <CheckboxField
                        id="folder-terms"
                        checked={termsCheckboxChecked}
                        onChange={(e) => setTermsCheckboxChecked(e.target.checked)}
                        checkboxColor={primary}
                        textColor={text}
                    >
                        Concordo com os{' '}
                        <a
                            href="https://sign.qitech.com.br/docs/termos_de_uso.pdf"
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: primary }}
                        >
                            Termos de Uso
                        </a>{' '}
                        e{' '}
                        <a
                            href="https://sign.qitech.com.br/docs/politica_de_privacidade.pdf"
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: primary }}
                        >
                            Política de Privacidade
                        </a>
                        .
                    </CheckboxField>

                    <CheckboxField
                        id="folder-inss"
                        checked={inssCheckboxChecked}
                        onChange={(e) => setInssCheckboxChecked(e.target.checked)}
                        checkboxColor={primary}
                        textColor={text}
                    >
                        Autorizo a consulta da margem consignável junto ao INSS para fins de contratação de crédito consignado.
                    </CheckboxField>
                </S.CheckboxGroup>

                <S.SubmitButton
                    $backgroundColor={primary}
                    $disabled={isSubmitDisabled}
                    disabled={isSubmitDisabled}
                    onClick={handleSubmit}
                >
                    {loading ? 'Carregando...' : 'ENTRAR'}
                    <ChevronRightIcon width={24} height={24} />
                </S.SubmitButton>
            </S.FormContainer>

            <S.Footer>
                <PoweredByQITech />
            </S.Footer>

            {openErrorDialog && (
                <ErrorDialog
                    description={openErrorDialog}
                    onClose={() => setOpenErrorDialog('')}
                    clientThemeSettings={resolvedTheme}
                />
            )}
        </S.PageWrapper>
    );
};
